<template>
  <div class="goods-card" v-resize @resize="onCardResize" ref="root" :style="cardStyle" @click="onClickCard" v-log-view
    @view="onCardView">
    <!-- 封面图 -->
    <div class="cover-wrapper">
      <aspect-ratio :value="1">
        <van-image class="cover" fit="cover" :src="item.imgUrl" />
      </aspect-ratio>
    </div>
    <div class="content">
      <!-- 标签 -->
      <div class="tags">
        <!-- p5 -->
        <div v-if="tag5" class="tag5">
          <div class="coupon" :style="{
            borderImageSource: `url(${tag5.tagImg})`
          }">
            {{ tag5.tagTitle }}
          </div>
        </div>
        <!-- p1 -->
        <div v-if="tag1s.length" class="tag1">
          <img v-for="tag in tag1s" height="16" fit="contain" class="tag-icon" :src="tag.tagImg" />
        </div>
        <!-- p2 -->
        <div v-if="tag2s.length" class="tag2">
          <img v-for="tag in tag2s" height="16" class="tag-icon" :src="tag.tagImg" />
        </div>
      </div>
      <!-- 评分 -->
      <div v-if="item.score" class="score-wrapper">
        <van-rate v-model="item.score" size="13" /><span v-if="item.commentNumber">({{ item.commentNumber }})</span>
      </div>
      <!-- 价格 -->
      <div class="price-wrapper">
        <span class="price">{{ formatPrice(item.price) }}</span>
        <span class="discount" v-if="item.marketPrice > item.price">{{ `-${calcDiscount(item)}%` }}</span>
      </div>
      <!-- p3 -->
      <div v-if="tag3" class="tag3">
        <img height="16" class="tag-icon" :src="tag3.tagImg" />
        <span>{{ tag3.tagTitle }}</span>
      </div>
      <div class="bottom">
        <span v-if="item.sold > 0" class="sold">{{ item.sold }}{{ $t('message.sold') }}</span>
        <div class="line">
          <!-- p4 -->
          <div class="tag4">
            <img v-for="tag in tag4s" height="16" class="tag-icon" :src="tag.tagImg" />
          </div>
          <!-- 加购 -->
          <Button class="btn-cart" @click.stop="onClickCart">
            <StaticImage class="icon" src="@/assets/images/goods/cart" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, inject } from 'vue'
import AspectRatio from './AspectRatio.vue'
import { useSetting } from '@/composables/use-setting'
import vResize from '@/directives/resize'
import { Env } from '@voghion/fe-util'
import { navigateTo, showSkuSelectorModal, logAFEvent } from '@voghion/native-api'
import { saveWindowScrollTop } from '@/utils/cache'
import StaticImage from './StaticImage.vue'
import { Button } from 'vant'

const pageName = inject('pageName')
const goodsSpm = inject('goodsSpm')
const goodsSpmName = inject('goodsSpmName')
const { formatPrice } = useSetting()
const env = Env.getInstance()

/**
 * 商品卡片
 */
const props = defineProps({
  /**
   * 商品数据
   */
  item: {
    type: Object,
    required: true,
    default () {
      return {}
    }
  },
  /**
   * 在列表中的索引值
   */
  index: {
    type: Number,
    default: 0
  }
})
const tagInfoMap = computed(() => {
  return props.item.tagInfoMap || {}
})
const tag5 = computed(() => {
  return tagInfoMap.value.P5 ? tagInfoMap.value.P5[0] : null
})
const tag1s = computed(() => {
  return tagInfoMap.value.P1 ? tagInfoMap.value.P1.slice(0, 2) : []
})
const tag2s = computed(() => {
  return tagInfoMap.value.P2 ? tagInfoMap.value.P2.slice(0, 2) : []
})
const tag3 = computed(() => {
  return tagInfoMap.value.P3 ? tagInfoMap.value.P3[0] : null
})
const tag4s = computed(() => {
  return tagInfoMap.value.P4 ? tagInfoMap.value.P4.slice(0, 2) : []
})
const calcDiscount = (item) => {
  return Math.floor(
    ((item.marketPrice - item.price) / item.marketPrice) *
    100,
  )
}
const root = ref()
const cardStyle = ref({})
const onCardResize = () => {
  const el = root.value;
  if (el) {
    const rows = Math.ceil(el.clientHeight / 5) + 2
    cardStyle.value.gridRowEnd = `span ${rows}`
  }
}
const onClickCard = () => {
  saveWindowScrollTop()
  logAFEvent({
    event: 'clickSPM',
    spm: goodsSpm,
    spmName: goodsSpmName,
    params: {
      goods_id: props.item.goodsId,
      index: props.index
    }
  })
  if (env.isEnvAppWebView) {
    navigateTo({
      url: `voghion://approuter/goodsDetail?goodsId=${props.item.goodsId}&prePage=${pageName || ''}`
    })
  }
}
const onClickCart = () => {
  if (env.isEnvAppWebView) {
    showSkuSelectorModal({
      goodsId: props.item.goodsId,
      confirmAction: 1,
      index: props.index,
      prePage: pageName
    })
  }
}
const onCardView = () => {
  logAFEvent({
    event: 'showSPM',
    spm: goodsSpm,
    spmName: goodsSpmName,
    params: {
      goods_id: props.item.goodsId,
      index: props.index
    }
  })
}
</script>

<style lang="scss" scoped>
.goods-card {
  border-radius: 8px;
  background: white;
  overflow: hidden;
  color: #333;

  .cover {
    width: 100%;
    height: 100%;

    :deep(.van-image__img) {
      border-radius: 8px;
    }

  }

  .content {
    padding: 8px 8px 12px;
  }

  .tag-icon {
    margin-right: 6px;
  }

  .tag5 {
    padding-bottom: 6px;

    .coupon {
      display: inline-block;
      padding: 0 8px 0 12px;
      border-image-slice: 0 20 0 0 fill;
      border-image-width: 0 10px 0 0;
      font-size: 10px;
      font-weight: 400;
      color: #ffffff;
    }
  }

  .tag1,
  .tag2,
  .tag4 {
    display: inline-flex;
    flex-wrap: wrap;
  }

  .tag3 {
    display: flex;
    align-items: center;

    span {
      margin-left: 4px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .score-wrapper {
    padding-top: 6px;
    font-size: 12px;
    display: flex;
    align-items: center;
    line-height: 17px;

    span {
      font-weight: 500;
      margin-left: 4px;
    }
  }

  .price-wrapper {
    padding-top: 4px;
    display: flex;
    align-items: center;

    .price {
      font-size: 16px;
      line-height: 19px;
      font-weight: bold;
    }

    .discount {
      font-size: 12px;
      line-height: 14px;
      background: #f3b847;
      border-radius: 2px;
      margin-left: 8px;
      padding: 0 2px;
    }
  }

  .bottom {
    margin-top: 4px;

    .sold {
      font-size: 12px;
      color: #999999;
      line-height: 14px;
    }

    .line {
      display: flex;
      justify-content: space-between;

      .btn-cart {
        border: none;
        padding-right: 0;

        &::before {
          background: transparent;
        }

        .icon {
          width: 38px;
          object-fit: contain;
        }
      }
    }
  }
}
</style>
